import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSite } from "../context/site";
import { PageHero, CtaBand, TrajectoryBand } from "../components/site/kit";
import { NextSteps, Pager } from "../components/site/extras";
import { Reveal } from "../components/site/motion";
import { useCases } from "../lib/casesApi";

const IMAGES = ["/assets/work-trajectory.jpg", "/assets/work-converge.jpg", "/assets/work-grid.jpg"];
const SITUATIONS = ["Growth Has Stalled", "Ready to Scale", "Reposition & Transform", "Create Enterprise Value"];

// Placeholders fill the grid until more named cases are approved for release.
const PLACEHOLDERS = Array.from({ length: 0 }, (_, i) => ({
  id: `ph-${i + 1}`,
  image: IMAGES[i % IMAGES.length],
  situation: SITUATIONS[i % SITUATIONS.length],
}));

export default function OurWork() {
  const { openDiagnostic } = useSite();
  const CASES = useCases();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawFilter = searchParams.get("situation");
  const activeFilter = SITUATIONS.includes(rawFilter) ? rawFilter : null;

  const setFilter = (situation) => {
    if (situation) setSearchParams({ situation }, { replace: false });
    else setSearchParams({}, { replace: false });
  };

  const filtered = activeFilter
    ? CASES.filter((c) => c.situation === activeFilter)
    : CASES;

  // On "All", the first case is featured; filtered views show a uniform grid.
  const lead = activeFilter ? null : filtered[0];
  const gridCases = activeFilter ? filtered : filtered.slice(1);

  let n = 1;
  const label = () => String(++n).padStart(2, "0");

  return (
    <div className="font-body bg-white">
      <PageHero
        testId="ourwork-hero"
        eyebrow="Our work"
        crumbs={[{ label: "Home", href: "/" }, { label: "Our Work" }]}
        title={<><span className="text-[#5f8f10]">Proof</span>, not promises.</>}
        subcopy="We measure our work by the growth it creates. A selection of the problems we have solved, and the outcomes they produced."
      />

      <section className="bg-[#F7F6F2]">
        <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
          {/* Filter bar */}
          <div data-testid="ourwork-filter-bar" className="mb-10 flex flex-col gap-5 border-b border-black/10 pb-8 md:mb-12 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                data-testid="ourwork-filter-all"
                onClick={() => setFilter(null)}
                aria-pressed={!activeFilter}
                className={`font-body text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 border px-4 py-2 ${!activeFilter ? "border-[#5f8f10] bg-[#5f8f10] text-white" : "border-black/20 bg-transparent text-black/60 hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}
              >
                All work
              </button>
              {SITUATIONS.map((s) => {
                const active = activeFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`ourwork-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    onClick={() => setFilter(s)}
                    aria-pressed={active}
                    className={`font-body text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 border px-4 py-2 ${active ? "border-[#5f8f10] bg-[#5f8f10] text-white" : "border-black/20 bg-transparent text-black/60 hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <p data-testid="ourwork-result-count" className="font-body shrink-0 text-xs font-semibold uppercase tracking-[0.12em] text-black/40">
              {filtered.length} {filtered.length === 1 ? "case" : "cases"}
            </p>
          </div>

          {/* Featured lead case (All view only) */}
          {lead && (
            <Link to={`/our-work/${lead.slug}`} data-testid="ourwork-lead" className="group relative block overflow-hidden bg-[#161A15]">
              <img src={lead.image} alt="" loading="lazy" decoding="async" className="aspect-[16/7] w-full object-cover opacity-70 grayscale transition-all duration-500 group-hover:scale-[1.02] group-hover:opacity-90 group-hover:grayscale-0" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(22,26,21,0) 40%, rgba(22,26,21,0.92) 100%)" }} />
              <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-6 p-8 md:p-12">
                <div>
                  <span className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">Featured / {lead.kicker}</span>
                  <h2 className="font-display mt-3 max-w-3xl text-3xl uppercase leading-[0.98] tracking-tight text-white md:text-5xl">{lead.headline}</h2>
                  <p className="font-body mt-3 max-w-md text-base font-normal text-white/75">{lead.client}</p>
                  <p className="font-body mt-4 text-sm font-semibold uppercase tracking-[0.12em] text-white/45">{lead.situation}</p>
                </div>
                <span aria-hidden className="mb-2 hidden shrink-0 text-3xl text-[#5f8f10] transition-transform duration-300 group-hover:translate-x-1 md:block">→</span>
              </div>
            </Link>
          )}

          {/* Case grid */}
          <div className={`grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 ${lead ? "mt-8" : ""}`}>
            {gridCases.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.08}>
                <Link to={`/our-work/${c.slug}`} data-testid={`ourwork-case-${c.slug}`} className="group flex h-full flex-col overflow-hidden border border-black/15 bg-white transition-colors duration-200 hover:border-[#5f8f10]">
                  <div className="overflow-hidden">
                    <img src={c.image} alt="" loading="lazy" decoding="async" className="aspect-[16/10] w-full object-cover grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">{label()} / {c.kicker}</span>
                    <h3 className="font-display mt-2 text-xl uppercase leading-[0.98] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10] md:text-2xl">{c.headline}</h3>
                    <p className="font-body mt-2 text-sm font-normal text-black/60">{c.client}</p>
                    <div className="mt-auto pt-5">
                      <span className="font-body block text-xs font-semibold uppercase tracking-[0.12em] text-black/40">{c.situation}</span>
                      <span className="font-body mt-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                        View case <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}

            {PLACEHOLDERS.map((w, i) => (
              <Reveal key={w.id} delay={((gridCases.length + i) % 3) * 0.08}>
                <div data-testid={`ourwork-placeholder-${i + 1}`} className="flex h-full flex-col overflow-hidden border border-black/15 bg-white">
                  <div className="overflow-hidden">
                    <img src={w.image} alt="" loading="lazy" decoding="async" className="aspect-[16/10] w-full object-cover opacity-90 grayscale" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-black/40">{label()}</span>
                    <h3 className="font-display mt-2 text-xl uppercase leading-[0.98] tracking-tight text-black md:text-2xl">[Outcome headline]</h3>
                    <p className="font-body mt-2 text-sm font-normal text-black/60">[Business descriptor]</p>
                    <div className="mt-auto pt-5">
                      <span className="font-body block text-xs font-semibold uppercase tracking-[0.12em] text-black/40">{w.situation}</span>
                      <span className="font-body mt-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-black/45">In preparation</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TrajectoryBand src="/assets/editorial-proof.jpg" testId="ourwork-band" label="Measured by the growth it creates" />

      <NextSteps links={[
        { eyebrow: "What we solve", title: "Four ways growth gets stuck", blurb: "Find the growth situation that sounds like your business.", href: "/what-we-solve" },
        { eyebrow: "How we work", title: "The SWITCH model", blurb: "The approach behind every result we create.", href: "/how-we-work" },
        { eyebrow: "Switchboard", title: "Thinking worth your time", blurb: "Perspectives on growth, scale and transformation.", href: "/insights" },
      ]} />

      <Pager current="/our-work" />

      <CtaBand
        heading="Could your business be the next case?"
        onClick={() => openDiagnostic()}
        testId="work-diagnostic-cta"
      />
    </div>
  );
}
